import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Search } from "lucide-react";
import { api, CATEGORY_ORDER } from "@/lib/api";
import { useSeo } from "@/lib/seo";
import { ArticleCard } from "@/components/ArticleCard";
import { AdSlot } from "@/components/AdSlot";

export default function Articles() {
  const [articles, setArticles] = useState([]);
  const [category, setCategory] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);

  useSeo({
    title: "Baccarat Dossiers — Rules, Odds & Strategy | Licence to Play",
    description:
      "Browse every baccarat briefing: rules, the third card rule, odds and house edge, betting strategy, live dealer basics and bankroll management.",
    keywords: "baccarat articles, baccarat guide, baccarat rules, baccarat strategy, baccarat odds",
  });

  useEffect(() => {
    setLoading(true);
    api
      .get("/articles", { params: { category, search: search || undefined, limit: 100 } })
      .then((r) => setArticles(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [category, search]);

  const cats = ["All", ...CATEGORY_ORDER];

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 py-14">
      <p className="overline text-gold mb-3">▸ The Archive</p>
      <h1 className="font-display text-4xl sm:text-5xl text-white tracking-tighter">Baccarat Dossiers</h1>
      <p className="mt-4 text-white/60 max-w-2xl leading-relaxed">
        Every briefing you need, from your first hand to advanced roadmap reading. Filter the archive or
        search the field notes.
      </p>

      <div className="mt-8 flex flex-col lg:flex-row lg:items-center gap-4 justify-between">
        <div className="flex flex-wrap gap-2">
          {cats.map((c) => (
            <button
              key={c}
              data-testid={`filter-${c.replace(/\s|&/g, "")}`}
              onClick={() => setCategory(c)}
              className={`px-4 py-2 text-sm font-medium border transition-colors ${
                category === c
                  ? "border-gold bg-gold text-black"
                  : "border-white/15 text-white/60 hover:text-white hover:border-white/40"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="relative w-full lg:w-72">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <input
            data-testid="article-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search briefings…"
            className="w-full bg-black/50 border border-white/20 text-white text-sm pl-10 pr-4 py-2.5 focus:border-gold focus:ring-1 focus:ring-gold/50 outline-none transition-colors"
          />
        </div>
      </div>

      <div className="my-8">
        <AdSlot size="728x90" label="Archive Leaderboard" />
      </div>

      {loading ? (
        <p className="font-mono text-sm text-white/40">Decrypting archive…</p>
      ) : articles.length === 0 ? (
        <p className="font-mono text-sm text-white/40" data-testid="no-articles">
          No briefings match your query.
        </p>
      ) : (
        <motion.div layout className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="articles-grid">
          {articles.map((a, i) => (
            <ArticleCard key={a.slug} article={a} index={i} />
          ))}
        </motion.div>
      )}
    </div>
  );
}
